import { useEffect, useRef, useState } from "react";

/**
 * useAutoRefresh — calls `refreshFn` on an interval while the tab is visible.
 * Skips while the tab is hidden to save battery / bandwidth.
 *
 * @param {Function} refreshFn - async function to invoke on each tick
 * @param {number} intervalMs  - polling interval (default 1s)
 * @param {boolean} enabled    - disable polling when false
 * @returns {{ isRefreshing: boolean, lastRefreshedAt: Date | null }}
 */
export default function useAutoRefresh(refreshFn, intervalMs = 1000, enabled = true) {
  const fnRef = useRef(refreshFn);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [lastRefreshedAt, setLastRefreshedAt] = useState(null);

  useEffect(() => { fnRef.current = refreshFn; }, [refreshFn]);

  useEffect(() => {
    if (!enabled) return undefined;
    let cancelled = false;

    const tick = async () => {
      if (document.hidden) return;
      try {
        setIsRefreshing(true);
        await fnRef.current?.();
        if (!cancelled) setLastRefreshedAt(new Date());
      } catch {
        // swallow — polling errors should not break UI
      } finally {
        if (!cancelled) setIsRefreshing(false);
      }
    };

    const id = setInterval(tick, intervalMs);
    const onVisible = () => { if (!document.hidden) tick(); };
    document.addEventListener("visibilitychange", onVisible);

    return () => {
      cancelled = true;
      clearInterval(id);
      document.removeEventListener("visibilitychange", onVisible);
    };
  }, [intervalMs, enabled]);

  return { isRefreshing, lastRefreshedAt };
}
